import React, { useState, useRef, useEffect, useMemo } from 'react';
import professionalSummaryData, { jobFamilies } from '../data/professionalSummaryData';
import { customToast } from '../utils/toastUtils';
import './ProfessionalSummaryGenerator.css';

const EXPERIENCE_RANGES = [
  { label: '0–1 Years', value: '0-1' },
  { label: '2–4 Years', value: '2-4' },
  { label: '5–7 Years', value: '5-7' },
  { label: '8+ Years', value: '8+' },
];

const TEMPLATE_LABELS = {
  withTitleAndSkills: 'Requires: Job Title & Skills',
  withTitleOnly: 'Requires: Job Title',
  generic: 'No extra fields needed',
};

const ProfessionalSummaryGenerator = ({ value, onChange }) => {
  const [experienceRange, setExperienceRange] = useState('');
  const [jobCategory, setJobCategory] = useState('');
  const [jobCategorySearch, setJobCategorySearch] = useState('');
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  const [highlightedDropdownIndex, setHighlightedDropdownIndex] = useState(-1);
  const [positionTitle, setPositionTitle] = useState('');
  const [skillsInput, setSkillsInput] = useState('');
  const [hasGenerated, setHasGenerated] = useState(false);
  const [isWritingOwn, setIsWritingOwn] = useState(false);
  const [generatedOptions, setGeneratedOptions] = useState([]); // Array of { key, label, text }
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);

  const jobDropdownRef = useRef(null);
  const jobInputRef = useRef(null);
  const dropdownListRef = useRef(null);
  const optionsRef = useRef(null);
  const formTopRef = useRef(null);
  const textareaRef = useRef(null);

  // If value is already populated (e.g. navigating back), auto-show the textarea
  useEffect(() => {
    if (value && value.trim().length > 0 && !isWritingOwn && selectedOptionIndex < 0 && !hasGenerated) {
      setIsWritingOwn(true);
    }
  }, []); // run once on mount

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (jobDropdownRef.current && !jobDropdownRef.current.contains(e.target)) {
        setShowJobDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter job families based on search
  const filteredFamilies = useMemo(() => {
    if (!jobCategorySearch.trim()) return jobFamilies;
    const search = jobCategorySearch.toLowerCase();
    const matches = jobFamilies.filter(f => f.toLowerCase().includes(search));
    // If no matches, show "Others" at top
    if (matches.length === 0) {
      return ['Others', ...jobFamilies.filter(f => f !== 'Others')];
    }
    return matches;
  }, [jobCategorySearch]);

  // Parse skills from comma-separated input (max 4)
  const parseSkills = (input) => {
    return input
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .slice(0, 4);
  };

  // Replace placeholders in a template string
  const applyReplacements = (template) => {
    const skills = parseSkills(skillsInput);
    const hasTitle = positionTitle.trim().length > 0;
    const hasSkills = skills.length > 0;

    let summary = template;
    summary = summary.replace(/\{JOB_TITLE\}/g, hasTitle ? positionTitle.trim() : '');
    summary = summary.replace(/\{SKILLS\}/g, hasSkills ? skills.join(', ') : '');
    // Clean up artifacts from empty replacements (double spaces, leading/trailing commas, etc.)
    summary = summary.replace(/\s{2,}/g, ' ');
    summary = summary.replace(/,\s*,/g, ',');
    summary = summary.replace(/\s+([.,;])/g, '$1');
    summary = summary.trim();
    return summary;
  };

  // Get the level data for the current inputs (with nearest-level fallback)
  const getLevelData = () => {
    const family = jobCategory || 'Others';
    const familyData = professionalSummaryData[family] || professionalSummaryData['Others'];
    if (!familyData) return null;

    let levelData = familyData[experienceRange];
    if (!levelData) {
      const availableLevels = Object.keys(familyData);
      const levelOrder = ['0-1', '2-4', '5-7', '8+'];
      const targetIdx = levelOrder.indexOf(experienceRange);
      let nearest = availableLevels[0];
      let minDist = Infinity;
      for (const avail of availableLevels) {
        const dist = Math.abs(levelOrder.indexOf(avail) - targetIdx);
        if (dist < minDist) { minDist = dist; nearest = avail; }
      }
      levelData = familyData[nearest];
    }
    return levelData;
  };

  // Determine the actual type of a template based on its placeholder content
  const getTemplateType = (tmpl) => {
    const hasTitle = tmpl.includes('{JOB_TITLE}');
    const hasSkills = tmpl.includes('{SKILLS}');
    if (hasTitle && hasSkills) return 'withTitleAndSkills';
    if (hasTitle) return 'withTitleOnly';
    return 'generic';
  };

  // Generate all matching template options
  const handleGenerate = () => {
    if (!experienceRange) {
      customToast.warning('Please select your experience range');
      return;
    }
    if (!jobCategory) {
      customToast.warning('Please select a job category');
      return;
    }

    const levelData = getLevelData();
    if (!levelData) {
      customToast.error('Unable to generate summary for this combination. Please try different inputs.');
      return;
    }

    // Show ALL templates from the data
    // Order: withTitleAndSkills first, then withTitleOnly, then generic
    const categoryOrder = ['withTitleAndSkills', 'withTitleOnly', 'generic'];

    const options = [];
    for (const category of categoryOrder) {
      const tmpl = levelData[category];
      if (tmpl && typeof tmpl === 'string' && tmpl.trim().length > 0) {
        // Determine actual type from content (not from key name which can be wrong)
        const actualType = getTemplateType(tmpl);
        options.push({
          key: category,
          actualType,
          label: TEMPLATE_LABELS[actualType] || actualType,
          rawTemplate: tmpl,
          text: applyReplacements(tmpl),
        });
      }
    }

    if (options.length === 0) {
      customToast.error('No templates available for this combination.');
      return;
    }

    setGeneratedOptions(options);
    setSelectedOptionIndex(-1);
    setHasGenerated(true);
    setIsWritingOwn(false);

    // Scroll to options
    setTimeout(() => {
      optionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const handleSelectOption = (index) => {
    const option = generatedOptions[index];
    const hasTitle = positionTitle.trim().length > 0;
    const hasSkills = parseSkills(skillsInput).length > 0;

    // Validate based on ACTUAL placeholder content in the template
    const actualType = option.actualType;

    if (actualType === 'withTitleAndSkills') {
      const missing = [];
      if (!hasTitle) missing.push('"Position/Job Title"');
      if (!hasSkills) missing.push('"Skills Known"');
      if (missing.length > 0) {
        customToast.warning(`Please fill in ${missing.join(' and ')} to use this template.`);
        return;
      }
    } else if (actualType === 'withTitleOnly') {
      if (!hasTitle) {
        customToast.warning('Please fill in "Position/Job Title" to use this template.');
        return;
      }
    }
    // generic (no placeholders): no validation needed, apply directly

    // Re-apply replacements with current input values
    const freshText = applyReplacements(option.rawTemplate);

    setSelectedOptionIndex(index);
    const updatedOptions = [...generatedOptions];
    updatedOptions[index] = { ...option, text: freshText };
    setGeneratedOptions(updatedOptions);
    onChange(freshText);
  };

  const handleWriteOwn = () => {
    setIsWritingOwn(true);
    setHasGenerated(false);
    setGeneratedOptions([]);
    setSelectedOptionIndex(-1);
    onChange('');
    // Scroll to the textarea after it becomes visible
    setTimeout(() => {
      textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      textareaRef.current?.querySelector('textarea')?.focus();
    }, 100);
  };

  const handleJobCategorySelect = (family) => {
    setJobCategory(family);
    setJobCategorySearch(family);
    setShowJobDropdown(false);
    setHighlightedDropdownIndex(-1);
  };

  // Keyboard navigation for job category dropdown
  const handleJobCategoryKeyDown = (e) => {
    if (!showJobDropdown) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setShowJobDropdown(true);
        setHighlightedDropdownIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedDropdownIndex(prev => {
          const next = prev < filteredFamilies.length - 1 ? prev + 1 : 0;
          // Scroll highlighted item into view
          setTimeout(() => {
            const list = dropdownListRef.current;
            if (list) {
              const items = list.querySelectorAll('.ps-dropdown-item');
              if (items[next]) items[next].scrollIntoView({ block: 'nearest' });
            }
          }, 0);
          return next;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedDropdownIndex(prev => {
          const next = prev > 0 ? prev - 1 : filteredFamilies.length - 1;
          setTimeout(() => {
            const list = dropdownListRef.current;
            if (list) {
              const items = list.querySelectorAll('.ps-dropdown-item');
              if (items[next]) items[next].scrollIntoView({ block: 'nearest' });
            }
          }, 0);
          return next;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedDropdownIndex >= 0 && highlightedDropdownIndex < filteredFamilies.length) {
          handleJobCategorySelect(filteredFamilies[highlightedDropdownIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowJobDropdown(false);
        setHighlightedDropdownIndex(-1);
        break;
      default:
        // Reset highlight when typing
        setHighlightedDropdownIndex(0);
        break;
    }
  };

  const handleSkillsChange = (val) => {
    // Limit to 4 skills
    const parts = val.split(',');
    if (parts.length > 4) {
      // Keep only first 4 + allow typing in 4th
      setSkillsInput(parts.slice(0, 4).join(','));
    } else {
      setSkillsInput(val);
    }
  };

  return (
    <div className="ps-generator" ref={formTopRef}>
      <div className="ps-generator-box">
        <h3 className="ps-generator-heading">
          <span className="ps-icon">📝</span>
          Professional Summary
        </h3>
        <p className="ps-generator-subtitle">
          Fill in the details below to generate a tailored professional summary, or write your own.
        </p>

        <div className="ps-form-grid">
          {/* Experience Range */}
          <div className="ps-form-group">
            <label className="ps-label">
              Experience Range <span className="ps-required">*</span>
            </label>
            <select
              className="ps-select"
              value={experienceRange}
              onChange={(e) => setExperienceRange(e.target.value)}
            >
              <option value="">Select experience range</option>
              {EXPERIENCE_RANGES.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          {/* Job Category - searchable dropdown */}
          <div className="ps-form-group" ref={jobDropdownRef}>
            <label className="ps-label">
              Job Category <span className="ps-required">*</span>
            </label>
            <div className="ps-searchable-dropdown">
              <input
                ref={jobInputRef}
                type="text"
                className="ps-input"
                placeholder="Search or select job category..."
                value={jobCategorySearch}
                onChange={(e) => {
                  setJobCategorySearch(e.target.value);
                  setShowJobDropdown(true);
                  setHighlightedDropdownIndex(0);
                  // If exact match, auto-select
                  const exact = jobFamilies.find(f => f.toLowerCase() === e.target.value.toLowerCase());
                  if (exact) {
                    setJobCategory(exact);
                  } else {
                    setJobCategory('');
                  }
                }}
                onFocus={() => { setShowJobDropdown(true); setHighlightedDropdownIndex(-1); }}
                onKeyDown={handleJobCategoryKeyDown}
              />
              {showJobDropdown && (
                <div className="ps-dropdown-list" ref={dropdownListRef}>
                  {filteredFamilies.map((family, idx) => (
                    <div
                      key={family}
                      className={`ps-dropdown-item ${jobCategory === family ? 'ps-dropdown-item-active' : ''} ${highlightedDropdownIndex === idx ? 'ps-dropdown-item-highlighted' : ''}`}
                      onClick={() => handleJobCategorySelect(family)}
                      onMouseEnter={() => setHighlightedDropdownIndex(idx)}
                    >
                      {family}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Position Title */}
          <div className="ps-form-group">
            <label className="ps-label">Which position are you looking for?</label>
            <input
              type="text"
              className="ps-input"
              placeholder="e.g. Software Engineer, Product Manager..."
              value={positionTitle}
              onChange={(e) => setPositionTitle(e.target.value)}
            />
          </div>

          {/* Skills */}
          <div className="ps-form-group">
            <label className="ps-label">
              Skills Known
              <span className="ps-hint-inline">(comma-separated, max 4)</span>
            </label>
            <input
              type="text"
              className="ps-input"
              placeholder="e.g. React, Node.js, Python, AWS"
              value={skillsInput}
              onChange={(e) => handleSkillsChange(e.target.value)}
            />
            {skillsInput && (
              <div className="ps-skills-tags">
                {parseSkills(skillsInput).map((skill, i) => (
                  <span key={i} className="ps-skill-tag">{skill}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="ps-button-row">
          <button
            className="ps-btn ps-btn-primary"
            onClick={handleGenerate}
            disabled={!experienceRange || !jobCategory}
          >
            {hasGenerated ? '🔄 Re-Generate Summary' : '✨ Generate Summary'}
          </button>
          <button
            className={`ps-btn ${isWritingOwn ? 'ps-btn-secondary-active' : 'ps-btn-secondary'}`}
            onClick={handleWriteOwn}
          >
            ✍️ Write Your Own
          </button>
        </div>
      </div>

      {/* Template Options — shown when templates are available */}
      {hasGenerated && generatedOptions.length > 0 && (
        <div className="ps-options-box" ref={optionsRef}>
          <div className="ps-options-header">
            <span className="ps-options-title">📋 Choose a Template</span>
            <span className="ps-options-hint">
              {selectedOptionIndex >= 0
                ? 'Selected! You can edit it below.'
                : 'Click on a template to use it. You can edit it after selecting.'}
            </span>
          </div>

          {/* Show requirement note if some templates need fields */}
          {generatedOptions.some(opt => opt.actualType !== 'generic') && (
            <div className="ps-options-requirements-note">
              <span>💡 </span>
              {(() => {
                const types = [...new Set(generatedOptions.map(o => o.actualType))];
                if (types.length === 1 && types[0] === 'withTitleAndSkills') {
                  return 'All templates below use your Job Title & Skills. Fill those fields above to select a template.';
                }
                if (types.includes('generic')) {
                  return 'Templates marked "✅ Ready" can be used without filling optional fields. Others need the indicated fields.';
                }
                return 'Fill in the required fields (Job Title / Skills) above to unlock templates that need them.';
              })()}
            </div>
          )}

          <div className="ps-options-list">
            {generatedOptions.map((opt, i) => (
              <div
                key={opt.key}
                className={`ps-option-card ${selectedOptionIndex === i ? 'ps-option-card-selected' : ''} ${opt.actualType === 'generic' ? 'ps-option-card-ready' : ''}`}
                onClick={() => handleSelectOption(i)}
              >
                <div className="ps-option-card-header">
                  <span className="ps-option-number">Template {i + 1}</span>
                  {opt.actualType === 'generic' && (
                    <span className="ps-option-ready">✅ Ready to use</span>
                  )}
                  {opt.actualType === 'withTitleOnly' && (
                    <>
                      {positionTitle.trim() ? (
                        <span className="ps-option-ready">✅ Ready</span>
                      ) : (
                        <span className="ps-option-needs">🏷️ Needs Job Title</span>
                      )}
                    </>
                  )}
                  {opt.actualType === 'withTitleAndSkills' && (
                    <>
                      {positionTitle.trim() && parseSkills(skillsInput).length > 0 ? (
                        <span className="ps-option-ready">✅ Ready</span>
                      ) : (
                        <>
                          {!positionTitle.trim() && <span className="ps-option-needs">🏷️ Needs Title</span>}
                          {parseSkills(skillsInput).length === 0 && <span className="ps-option-needs">🛠️ Needs Skills</span>}
                        </>
                      )}
                    </>
                  )}
                  {selectedOptionIndex === i && <span className="ps-option-check">✓ Selected</span>}
                </div>
                <p className="ps-option-preview">{opt.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Output — only visible when writing own or a template is selected */}
      {(isWritingOwn || selectedOptionIndex >= 0) && (
        <div className="ps-output-box" ref={textareaRef}>
          <label className="ps-label">Your Professional Summary</label>
          <textarea
            className="ps-textarea"
            rows="8"
            placeholder={isWritingOwn
              ? "Write your professional summary here..."
              : "You can edit the selected template below."
            }
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          <p className="ps-hint">
            {value ? `${value.length} characters` : '2-3 sentences that showcase your expertise and career highlights'}
          </p>
        </div>
      )}
    </div>
  );
};

export default ProfessionalSummaryGenerator;
